/** Same-origin JSON API. A 401 means the session is gone: the app shows the login screen. */
import type { ImportReport, PullResult, PushRequest, PushResult, Snapshot } from '@solar/core'

export class Unauthorized extends Error {
  constructor() {
    super('unauthorized')
    this.name = 'Unauthorized'
  }
}

export interface User {
  readonly id: string
  readonly username: string
}

async function req<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(path, {
    method: init.method ?? 'GET',
    credentials: 'same-origin',
    headers: init.body === undefined ? {} : { 'content-type': 'application/json' },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  })
  if (res.status === 401) throw new Unauthorized()
  if (!res.ok) {
    let msg = `${res.status}`
    try {
      msg = ((await res.json()) as { error?: string }).error ?? msg
    } catch {
      // not JSON
    }
    throw new Error(msg)
  }
  return (await res.json()) as T
}

export interface PasskeyInfo {
  readonly id: string
  readonly name: string
  readonly createdAt: number
  readonly lastUsedAt: number | null
}

export interface ImportResult {
  readonly report: ImportReport
  readonly transactions: number
  readonly ops: number
  readonly rejected: readonly string[]
}

export const api = {
  importV1: (dump: unknown, dry: boolean) => req<ImportResult>(`/api/import/v1${dry ? '?dry=1' : ''}`, { method: 'POST', body: dump }),
  me: () => req<{ user: User }>('/api/me'),
  changePassword: (current: string, next: string) => req<{ ok: true }>('/api/auth/password', { method: 'POST', body: { current, next } }),
  passkeys: () => req<{ passkeys: PasskeyInfo[] }>('/api/passkeys'),
  passkeyRegistrationOptions: () => req<unknown>('/api/passkeys/options', { method: 'POST' }),
  addPasskey: (response: unknown, name: string) => req<{ id: string }>('/api/passkeys', { method: 'POST', body: { response, name } }),
  removePasskey: (id: string) => req<{ ok: true }>(`/api/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  passkeySignInOptions: () => req<unknown>('/api/auth/passkey/options', { method: 'POST' }),
  passkeySignIn: (response: unknown) => req<{ user: User }>('/api/auth/passkey/verify', { method: 'POST', body: { response } }),
  login: (username: string, password: string) => req<{ user: User }>('/api/auth/login', { method: 'POST', body: { username, password } }),
  logout: () => req<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  snapshot: () => req<Snapshot>('/api/snapshot'),
  push: (body: PushRequest) => req<PushResult>('/api/ops', { method: 'POST', body }),
  pull: (since: number) => req<PullResult>(`/api/ops?since=${since}`),
}
